import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  LuSparkles,
  LuScissors,
  LuScissorsLineDashed,
  LuArrowLeftToLine,
  LuTrash2,
  LuX,
  LuChevronRight,
  LuChevronDown,
  LuPlay,
  LuRepeat,
  LuAlignHorizontalJustifyStart,
  LuAlignHorizontalJustifyEnd,
  LuLink,
  LuClapperboard,
} from "react-icons/lu";
import { MdAnimation } from "react-icons/md";
import i18n from "i18next";
import Waveform from "../Waveform";
import { computeSnapThresholdFrames, resolveStickySnap } from "../../utils/snap";
import { createEdgeAutoScroll } from "../../utils/edgeAutoScroll";
import { minSourceStartFrame, maxSourceEndFrame } from "../../utils/sourceBounds";
import { openInDirector } from "../../../../carousel/audio/video-director/openInDirector";
import "./TrackClip.scss";

const AUDIO_TYPES = new Set(["audio", "scene-audio"]);
const MEDIA_TYPES = new Set(["audio", "scene-audio", "video"]);
const MIN_CLIP_SECONDS = 0.1;
// Below this the clip is a sliver: the two time badges would fill it end to
// end and leave nothing for the name, so they drop out instead.
const TIME_BADGE_MIN_PX = 108;
const ROW_DRAG_PX = 14; // vertical movement before a clip-body drag also reorders its row
const SWAP_DRAG_PX = 18;
// Context menu at its tallest/widest (icon row + a two-line caption). The menu
// is placed ONCE against these, and is always anchored by its top-left corner,
// so the caption appearing on hover can only grow it downward. A menu that
// grows upward slides the hovered button out from under the cursor, which ends
// the hover, shrinks the menu, and puts the button back — a flicker loop.
const MENU_MAX_H = 130;
const MENU_MAX_W = 300;
const MENU_LABELED_MAX_H = 340; // labeled menus are one column
const MENU_MARGIN = 8;
// Body portal (a transformed ancestor would become the fixed origin and clip it), above the dialog band.
const MENU_Z = 3000050;

function TrackClip({
  layer,
  clipLeft,
  clipWidth,
  isSelected,
  isNewFlash,
  isDragSource,
  color,
  pps,
  fps,
  onUpdate,
  onUpdateEnd,
  onSplit,
  onTrimHead,
  currentFrame,
  onDelete,
  onToggleLoop,
  onToggleVideoLoop,
  onMerlin,
  onAnimation,
  onDoubleClick,
  expandable = false,
  expanded = false,
  onToggleExpand,
  noResize,
  snapTargets,
  onSnapGuideChange,
  onBeginClipDrag,
  onSelect,
  menuSelects = true, // false: right-click opens the menu without selecting the clip
  rowNeighborBounds,
  hasPrevNeighbor,
  hasNextNeighbor,
  onAlignToNeighbor,
  isSplitHighlighted,
  onSplitGroupHover,
  extraItems, // [{ key, icon, label, hint?, disabled?, onClick }] — host-supplied menu actions
  menuLabels, // { [key]: text } — when set the menu shows a text label beside each icon
}) {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null); // 'left' | 'right' | null
  const [contextMenu, setContextMenu] = useState(null); // { x, y }
  // The context-menu action under the cursor — { key, label, hint }. The icon
  // row is otherwise anonymous, so the menu carries one caption line naming
  // whatever is hovered instead of relying on native tooltips alone. It is
  // cleared only when the cursor leaves the whole menu, never button by
  // button: the 2 px gap between two buttons would otherwise blank the caption
  // for a frame every time the cursor crosses it.
  const [menuHint, setMenuHint] = useState(null);
  // Head-trim ("kes ve sil") is destructive and the amount it removes depends
  // on where the playhead sits, so hovering its button paints the doomed slice
  // straight onto the clip before the author commits.
  const trimHeadHover = menuHint?.key === "trim-head";
  const [audioNameTip, setAudioNameTip] = useState(null); // { left, top, below }
  const audioNameTipRef = useRef(null);

  const [pwPending, setPwPending] = useState(null);
  const pwPendingRef = useRef(null);
  const pendingRef = useRef(null);
  const isAudio = AUDIO_TYPES.has(layer.type);
  const isMedia = MEDIA_TYPES.has(layer.type);
  const isSceneAudio = layer.type === "scene-audio";
  // Scene-audio's "trim" is expressed differently (audioOffset, seconds into
  // the source file) than a regular audio box (trimStart, frames) — the
  // waveform always shows exactly the slice this clip plays, so split
  // siblings of the same source file look visually distinct.
  const waveformUrl = isSceneAudio
    ? layer.audioData?.file || layer.audioData?.url || layer.audioData?.src
    : layer.mediaUrl;
  const waveformTrimStartSec = isSceneAudio
    ? parseFloat(layer.audioData?.audioOffset) || 0
    : (layer.trimStart ?? 0) / fps;
  const waveformTrimEndSec = waveformTrimStartSec + (layer.endFrame - layer.startFrame) / fps;
  const isVideo = layer.type === "video";
  const isAutoPlayVideo = isVideo && layer.autoPlay;
  const minFrames = Math.ceil(fps * MIN_CLIP_SECONDS);
  const toSec = (f) => (f / fps).toFixed(2);
  const snapThresholdFrames = computeSnapThresholdFrames(pps, fps);

  // ── Head trim ("kes ve sil") ───────────────────────────
  // Cut at the playhead, drop the piece before it and pull the rest back to
  // the clip's own start. Same in-clip guard the split uses, so whenever the
  // split is offered this is too — and vice versa.
  const cutFrame = typeof currentFrame === "number" ? currentFrame : null;
  const playheadInsideClip =
    cutFrame !== null && cutFrame > layer.startFrame + 1 && cutFrame < layer.endFrame - 1;
  const canTrimHead = !!onTrimHead && isMedia && !layer.locked && playheadInsideClip;
  const trimHeadFrames = canTrimHead ? cutFrame - layer.startFrame : 0;

  // Time badges. On a labelled clip they sit IN FLOW, so the name is bounded by
  // them and can never be drawn underneath — absolutely positioned, they shared
  // the label's vertical band and covered it on any short clip. Audio clips keep
  // them floating: there is no label to protect there, and the waveform reads
  // better full-bleed than squeezed between two chips.
  const showTimeBadges = clipWidth >= TIME_BADGE_MIN_PX;
  const badgeFlow = isAudio ? "" : " it-clip-badge--flow";
  const trimHeadWidth = Math.min(clipWidth, (trimHeadFrames / fps) * pps);

  const closeContextMenu = () => {
    setContextMenu(null);
    setMenuHint(null);
  };

  const audioTipPosition = (clientX, clientY) => ({
    left: Math.max(116, Math.min(window.innerWidth - 116, clientX)),
    top: clientY < 56 ? clientY + 16 : clientY - 12,
    below: clientY < 56,
  });

  const moveAudioTip = (clientX, clientY) => {
    const next = audioTipPosition(clientX, clientY);
    const tip = audioNameTipRef.current;
    if (!tip) return;
    tip.style.left = `${next.left}px`;
    tip.style.top = `${next.top}px`;
    tip.classList.toggle("it-audio-name-tip--below", next.below);
  };

  // ── Clip body drag ─────────────────────────────────────
  const handleClipPointerDown = (e) => {
    setAudioNameTip(null);
    if (e.button !== 0) {
      // Right-click only opens the context menu: no select, no drag, no ruler seek.
      e.stopPropagation();
      return;
    }
    onSelect?.(layer.id);
    if (layer.locked) return;
    if (e.target.closest(".it-clip-resize")) return;
    if (e.target.closest(".it-clip-expander")) return;
    if (e.target.closest(".it-play-window")) return;
    e.stopPropagation();

    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    const startX = e.clientX;
    const startY = e.clientY;
    const origStart = layer.startFrame;
    const origEnd = layer.endFrame;
    // Interactive clips have a fixed 1-second logical duration — don't inherit
    // the (potentially huge) scene-duration endFrame as clipLen.
    const clipLen = noResize ? fps : origEnd - origStart;
    let didMove = false;
    // Lazily hand off to the row-drop session after a vertical drag.
    let rowSession = null;
    let rowSessionAttempted = false;

    setDragging(true);
    pendingRef.current = { startFrame: origStart, endFrame: origEnd };

    const targets = snapTargets || [];
    const stickyStart = { current: null };
    const stickyEnd = { current: null };

    let lastClientX = startX;
    let lastClientY = startY;
    const scroller = createEdgeAutoScroll(el, () => moveCore());

    const moveCore = () => {
      if (!rowSessionAttempted && Math.abs(lastClientY - startY) > ROW_DRAG_PX) {
        rowSessionAttempted = true;
        rowSession = onBeginClipDrag?.(layer) || null;
        if (rowSession && didMove) {
          // Undo horizontal drift accumulated before the handoff.
          pendingRef.current = { startFrame: origStart, endFrame: origEnd };
          onUpdate(layer.id, origStart, origEnd);
          onSnapGuideChange?.(null);
          didMove = false;
        }
      }
      if (rowSession) {
        const desiredStart = Math.max(
          0,
          origStart + Math.round(((lastClientX - startX + scroller.scrollDelta()) / pps) * fps)
        );
        rowSession.onMove(lastClientX, lastClientY + scroller.scrollDeltaY(), desiredStart);
        return;
      }

      const dFrames = Math.round(((lastClientX - startX + scroller.scrollDelta()) / pps) * fps);
      if (dFrames === 0) return;
      didMove = true;
      // No marker clamp: a clip may live beyond the duration marker
      let newStart = Math.max(0, origStart + dFrames);
      let newEnd = newStart + clipLen;

      // Snap whichever edge lands closest to a sibling/playhead/marker frame,
      // then shift the whole clip so its length stays fixed.
      const snapStart = resolveStickySnap(newStart, targets, snapThresholdFrames, stickyStart);
      const snapEnd = resolveStickySnap(newEnd, targets, snapThresholdFrames, stickyEnd);
      let snappedFrame = null;
      if (snapStart != null || snapEnd != null) {
        const startDist = snapStart != null ? Math.abs(newStart - snapStart) : Infinity;
        const endDist = snapEnd != null ? Math.abs(newEnd - snapEnd) : Infinity;
        if (startDist <= endDist) {
          newStart = Math.max(0, snapStart);
          newEnd = newStart + clipLen;
          snappedFrame = snapStart;
        } else {
          newEnd = snapEnd;
          newStart = Math.max(0, newEnd - clipLen);
          snappedFrame = snapEnd;
        }
      }
      // Crossing a sibling far enough switches to ripple insertion.
      if (rowNeighborBounds) {
        const minStart = rowNeighborBounds.prevEnd ?? 0;
        const maxStart =
          rowNeighborBounds.nextStart != null ? rowNeighborBounds.nextStart - clipLen : Infinity;
        const clamped = Math.min(Math.max(newStart, minStart), Math.max(minStart, maxStart));
        if (clamped !== newStart) {
          const overshootPx = (Math.abs(newStart - clamped) / fps) * pps;
          if (!rowSessionAttempted && overshootPx > SWAP_DRAG_PX) {
            rowSessionAttempted = true;
            rowSession = onBeginClipDrag?.(layer) || null;
            if (rowSession) {
              pendingRef.current = { startFrame: origStart, endFrame: origEnd };
              onUpdate(layer.id, origStart, origEnd);
              onSnapGuideChange?.(null);
              didMove = false;
              moveCore();
              return;
            }
          }
          newStart = clamped;
          newEnd = newStart + clipLen;
          snappedFrame = null;
        }
      }
      onSnapGuideChange?.(snappedFrame);

      pendingRef.current = { startFrame: newStart, endFrame: newEnd };
      onUpdate(layer.id, newStart, newEnd);
    };

    const onMove = (me) => {
      lastClientX = me.clientX;
      lastClientY = me.clientY;
      scroller.update(me.clientX, me.clientY);
      moveCore();
    };

    const onUp = () => {
      setDragging(false);
      scroller.stop();
      onSnapGuideChange?.(null);
      if (rowSession) rowSession.commit();
      if (didMove) {
        const { startFrame, endFrame } = pendingRef.current;
        console.log(
          `🎬 [IdeaTimeline] Drag — "${layer.name}"\n` +
            `   Başlangıç: ${toSec(startFrame)}s  |  Bitiş: ${toSec(endFrame)}s  |  Süre: ${toSec(endFrame - startFrame)}s`
        );
        onUpdateEnd?.(layer.id, startFrame, endFrame);
      }
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  // ── Resize left (trim start) ───────────────────────────
  const handleResizeLeft = (e) => {
    if (layer.locked) return;
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    const startX = e.clientX;
    const origStart = layer.startFrame;
    const fixedEnd = layer.endFrame;
    const origTrimStart = layer.trimStart ?? 0;
    const origAudioOffset = parseFloat(layer.audioData?.audioOffset) || 0;
    // Audio can only uncover the head an earlier trim hid — dragging past the
    // source's first sample used to grow the clip with nothing to play there,
    // which slid the sound earlier and stretched its waveform.
    const minStart = isAudio ? minSourceStartFrame(layer, fps) : 0;
    let didMove = false;

    setResizing("left");
    pendingRef.current = {
      startFrame: origStart,
      endFrame: fixedEnd,
      trimStart: origTrimStart,
      audioOffset: origAudioOffset,
    };
    const targets = snapTargets || [];
    const stickySnap = { current: null };

    let lastClientX = startX;
    const scroller = createEdgeAutoScroll(el, () => moveCore());

    const moveCore = () => {
      const dFrames = Math.round(((lastClientX - startX + scroller.scrollDelta()) / pps) * fps);
      if (dFrames === 0) return;
      didMove = true;
      let newStart = Math.max(minStart, Math.min(fixedEnd - minFrames, origStart + dFrames));
      const snapped = resolveStickySnap(newStart, targets, snapThresholdFrames, stickySnap);
      if (snapped != null) newStart = Math.max(minStart, Math.min(fixedEnd - minFrames, snapped));
      let guideFrame = newStart === snapped ? snapped : null;
      if (rowNeighborBounds?.prevEnd != null && newStart < rowNeighborBounds.prevEnd) {
        newStart = Math.min(fixedEnd - minFrames, rowNeighborBounds.prevEnd);
        guideFrame = null;
      }
      onSnapGuideChange?.(guideFrame);
      // Video: the outer clip is stage presence — decoupled from the source, so
      // moving its start does NOT trim the media. Audio keeps the coupling:
      // narration expresses its in-point in seconds (audioOffset), an audio box
      // in frames (trimStart).
      const newTrimStart = isAudio
        ? Math.max(0, origTrimStart + (newStart - origStart))
        : origTrimStart;
      const newAudioOffset = Math.max(0, origAudioOffset + (newStart - origStart) / fps);
      pendingRef.current = {
        startFrame: newStart,
        endFrame: fixedEnd,
        trimStart: newTrimStart,
        audioOffset: newAudioOffset,
      };
      // The in-point rides along live, so the waveform keeps showing the slice
      // that will actually play instead of jumping on release.
      const liveExtra = isSceneAudio
        ? { audioData: { ...(layer.audioData || {}), audioOffset: newAudioOffset } }
        : isAudio
          ? { trimStart: newTrimStart }
          : {};
      onUpdate(layer.id, newStart, fixedEnd, liveExtra);
    };

    const onMove = (me) => {
      lastClientX = me.clientX;
      scroller.update(me.clientX);
      moveCore();
    };

    const onUp = () => {
      setResizing(null);
      scroller.stop();
      onSnapGuideChange?.(null);
      if (didMove) {
        const { startFrame, endFrame, trimStart, audioOffset } = pendingRef.current;
        // The host's scene-audio branch persists audioOffset only — a trimStart
        // there was silently dropped, so the sound slid instead of trimming.
        const extra = isVideo ? {} : isSceneAudio ? { audioOffset } : { trimStart };
        onUpdateEnd?.(layer.id, startFrame, endFrame, extra);
      }
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  // ── Resize right (trim end) ────────────────────────────
  const handleResizeRight = (e) => {
    if (layer.locked) return;
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    const startX = e.clientX;
    const origEnd = layer.endFrame;
    const fixedStart = layer.startFrame;
    const origTrimEnd = layer.trimEnd ?? null;
    let didMove = false;

    setResizing("right");
    pendingRef.current = { startFrame: fixedStart, endFrame: origEnd, trimEnd: origTrimEnd };
    const targets = snapTargets || [];
    const stickySnap = { current: null };

    let lastClientX = startX;
    const scroller = createEdgeAutoScroll(el, () => moveCore());

    const moveCore = () => {
      const dFrames = Math.round(((lastClientX - startX + scroller.scrollDelta()) / pps) * fps);
      if (dFrames === 0) return;
      didMove = true;
      let newEnd = Math.max(fixedStart + minFrames, origEnd + dFrames);
      const snapped = resolveStickySnap(newEnd, targets, snapThresholdFrames, stickySnap);
      if (snapped != null) newEnd = Math.max(fixedStart + minFrames, snapped);
      // Video: stage presence is independent of media length — no clamp, so a
      // short video can sit on stage for the whole scene. Audio stops at the
      // source's last sample (see utils/sourceBounds).
      if (isAudio) {
        const maxEnd = maxSourceEndFrame(layer, fps);
        if (maxEnd !== null) newEnd = Math.min(newEnd, Math.max(fixedStart + minFrames, maxEnd));
      }
      onSnapGuideChange?.(newEnd === snapped ? snapped : null);
      // An audio clip plays its source linearly, so its out-point is always its
      // in-point plus its length — the same rule a split uses. Offsetting the
      // old trimEnd instead went wrong whenever it was unknown (`null + delta`)
      // or the clip had already outgrown its source.
      const newTrimEnd = isAudio ? (layer.trimStart ?? 0) + (newEnd - fixedStart) : origTrimEnd;
      pendingRef.current = { startFrame: fixedStart, endFrame: newEnd, trimEnd: newTrimEnd };
      onUpdate(layer.id, fixedStart, newEnd, { resizeEdge: "right" });
    };

    const onMove = (me) => {
      lastClientX = me.clientX;
      scroller.update(me.clientX);
      moveCore();
    };

    const onUp = () => {
      setResizing(null);
      scroller.stop();
      onSnapGuideChange?.(null);
      if (didMove) {
        const { startFrame, endFrame, trimEnd } = pendingRef.current;
        onUpdateEnd?.(
          layer.id,
          startFrame,
          endFrame,
          isVideo
            ? { userSetEndFrame: true, resizeEdge: "right" }
            : { trimEnd, userSetEndFrame: true, resizeEdge: "right" }
        );
      }
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  // ── Inline play-window bar (autoPlay video) ───────────────
  // The outer clip is stage presence; this inset bar is the media segment that
  // actually plays. Body drag = when it plays (playStartFrame); left grip =
  // source in-point (trimStart, right scene-edge fixed); right grip = source
  // out-point (trimEnd, i.e. play length, capped at media length).
  const mediaFrames = layer.mediaDurationFrames ?? null;
  const clipFrames = layer.endFrame - layer.startFrame;
  // gif-style videos often report no finite duration → mediaDurationFrames is
  // null. We still offer a play window, seeding a sensible default length the
  // author can resize (only the right/length grip; source-trim needs a known
  // duration, so that left grip stays hidden).
  const hasKnownDuration = typeof mediaFrames === "number" && mediaFrames > 0;
  const defaultUnknownLen = Math.max(minFrames, Math.round(clipFrames * 0.6));

  const pwTrimStart = pwPending?.trimStart ?? layer.trimStart ?? 0;
  const rawTrimEnd = pwPending?.trimEnd ?? layer.trimEnd ?? mediaFrames;
  const pwTrimEnd = typeof rawTrimEnd === "number" ? rawTrimEnd : pwTrimStart + defaultUnknownLen;
  const pwPlayLen = Math.max(minFrames, pwTrimEnd - pwTrimStart);
  let pwPlayStart = pwPending?.playStart ?? layer.playStartFrame ?? layer.startFrame;
  pwPlayStart = Math.max(
    layer.startFrame,
    Math.min(pwPlayStart, Math.max(layer.startFrame, layer.endFrame - pwPlayLen))
  );
  const pwLeft = ((pwPlayStart - layer.startFrame) / fps) * pps;
  const pwWidth = Math.max(6, (pwPlayLen / fps) * pps);

  // Keep the full play-window interactive only on the selected clip.
  const PW_SHOW_MARGIN = Math.round(fps * 0.5);
  // Looped videos fill the whole clip, so the play-window segment is meaningless.
  const showPlayWindow = isAutoPlayVideo && !layer.loop;
  const pwIsFull = clipFrames - pwPlayLen <= PW_SHOW_MARGIN;

  const commitPlayWindow = (next) => {
    const extra = { playStartFrame: next.playStart, trimStart: next.trimStart };
    if (typeof next.trimEnd === "number") extra.trimEnd = next.trimEnd;
    onUpdateEnd?.(layer.id, layer.startFrame, layer.endFrame, extra);
  };

  const beginPwDrag = (e, compute) => {
    if (layer.locked) return;
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const base = { playStart: pwPlayStart, trimStart: pwTrimStart, trimEnd: pwTrimEnd };
    let moved = false;
    pwPendingRef.current = base;
    const onMove = (me) => {
      const d = Math.round(((me.clientX - startX) / pps) * fps);
      if (d === 0) return;
      moved = true;
      const next = compute(d, base);
      pwPendingRef.current = next;
      setPwPending(next);
    };
    const onUp = () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      if (moved && pwPendingRef.current) commitPlayWindow(pwPendingRef.current);
      setPwPending(null);
      pwPendingRef.current = null;
    };
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
  };

  const handlePwPointerDown = (e) => {
    if (e.target.closest(".it-play-window-resize")) return;
    const maxStart = Math.max(layer.startFrame, layer.endFrame - pwPlayLen);
    beginPwDrag(e, (d, base) => ({
      ...base,
      playStart: Math.max(layer.startFrame, Math.min(maxStart, base.playStart + d)),
    }));
  };

  const handlePwResizeLeft = (e) =>
    beginPwDrag(e, (d, base) => {
      // right scene-edge fixed → playStart follows trimStart; keep playStart >= clip start
      const minTs = Math.max(0, base.trimStart + (layer.startFrame - base.playStart));
      let nts = base.trimStart + d;
      nts = Math.max(minTs, Math.min(nts, base.trimEnd - minFrames));
      return {
        playStart: base.playStart + (nts - base.trimStart),
        trimStart: nts,
        trimEnd: base.trimEnd,
      };
    });

  const handlePwResizeRight = (e) =>
    beginPwDrag(e, (d, base) => {
      const maxByClip = base.trimStart + (layer.endFrame - base.playStart);
      let nte = base.trimEnd + d;
      nte = Math.max(base.trimStart + minFrames, nte);
      nte = Math.min(nte, maxByClip);
      if (mediaFrames != null) nte = Math.min(nte, mediaFrames);
      return { ...base, trimEnd: nte };
    });

  // ── Animasyon gösterge barları ────────────────────────────
  const clipDuration = (layer.endFrame - layer.startFrame) / fps;
  const entryDuration = layer.boxAnimation?.duration ?? 0;
  const exitDuration = layer.boxExitAnimation?.duration ?? 0;
  const hasEntry = !!layer.boxAnimation?.animation;
  const hasExit = !!layer.boxExitAnimation?.animation;

  const entryW =
    hasEntry && clipDuration > 0
      ? Math.min(clipWidth * 0.45, (entryDuration / clipDuration) * clipWidth)
      : 0;
  const exitW =
    hasExit && clipDuration > 0
      ? Math.min(clipWidth * 0.45, (exitDuration / clipDuration) * clipWidth)
      : 0;

  // ── Context menu ───────────────────────────────────────
  // One icon button. The row is icon-only, so each button also feeds the
  // caption line below it. Unavailable actions use aria-disabled rather than
  // the native attribute: a natively disabled button fires no mouse events, so
  // it could never explain in the caption WHY it is unavailable.
  const menuBtn = ({ key, icon, label: defaultLabel, hint, tone, disabled, onClick }) => {
    const label = menuLabels?.[key] ?? defaultLabel;
    const describe = () => setMenuHint({ key, label, hint });
    return (
      <button
        key={key}
        type="button"
        className={[
          "it-clip-context-menu__icon-btn",
          tone ? `it-clip-context-menu__icon-btn--${tone}` : "",
        ]
          .join(" ")
          .trim()}
        aria-disabled={disabled ? "true" : undefined}
        aria-label={label}
        title={label}
        onMouseEnter={describe}
        onFocus={describe}
        onClick={() => {
          if (disabled) return;
          onClick();
          closeContextMenu();
        }}
      >
        {icon}
        {menuLabels && <span className="it-clip-context-menu__text">{label}</span>}
      </button>
    );
  };

  // Actions in semantic clusters, separated by a hairline: AI/animation,
  // cutting, playback, alignment, then the destructive one. Only built while
  // the menu is open — this component re-renders on every playback frame.
  const menuGroups = [];
  const pushMenuGroup = (buttons) => {
    const group = buttons.filter(Boolean);
    if (group.length > 0) menuGroups.push(group);
  };

  if (contextMenu) {
    pushMenuGroup([
      layer.type === "image" &&
        onMerlin &&
        menuBtn({
          key: "merlin",
          icon: <LuSparkles />,
          label: "Aidea Wizard AI",
          tone: "merlin",
          onClick: () => onMerlin(layer.id),
        }),
      onAnimation &&
        !isMedia &&
        menuBtn({
          key: "animation",
          icon: <MdAnimation />,
          label: "Animasyon",
          onClick: () => onAnimation(layer.id),
        }),
      layer.videoProjectId &&
        menuBtn({
          key: "open-director",
          icon: <LuClapperboard />,
          label: i18n.t("OpenInVideoDirector"),
          onClick: () => openInDirector({ projectId: layer.videoProjectId, targetBoxId: layer.id }),
        }),
    ]);

    pushMenuGroup([
      onSplit &&
        isMedia &&
        menuBtn({
          key: "split",
          icon: <LuScissors />,
          label: i18n.t("SplitAtPlayhead") || "Playhead'den Böl",
          hint: playheadInsideClip
            ? i18n.t("SplitAtPlayheadHint")
            : i18n.t("CutPlayheadOutsideHint"),
          disabled: !playheadInsideClip,
          onClick: () => onSplit(layer.id),
        }),
      onTrimHead &&
        isMedia &&
        !layer.locked &&
        menuBtn({
          key: "trim-head",
          icon: <LuScissorsLineDashed />,
          label: i18n.t("CutAndDelete") || "Kes ve Sil",
          hint: playheadInsideClip ? i18n.t("CutAndDeleteHint") : i18n.t("CutPlayheadOutsideHint"),
          tone: "cut",
          disabled: !playheadInsideClip,
          onClick: () => onTrimHead(layer.id),
        }),
    ]);

    pushMenuGroup([
      onToggleLoop &&
        layer.type === "scene-audio" &&
        menuBtn({
          key: "loop-scene",
          icon: <LuRepeat />,
          label: i18n.t("messages.trackClip.repeatForScene"),
          tone: layer.audioData?.loopScene ? "active" : null,
          onClick: () => onToggleLoop(layer.id),
        }),
      onToggleVideoLoop &&
        isVideo &&
        menuBtn({
          key: "loop-video",
          icon: <LuRepeat />,
          label: i18n.t("Loop") || "Döngü",
          tone: layer.loop ? "active" : null,
          onClick: () => onToggleVideoLoop(layer.id),
        }),
    ]);

    pushMenuGroup([
      onAlignToNeighbor &&
        !layer.locked &&
        menuBtn({
          key: "align-prev",
          icon: <LuAlignHorizontalJustifyStart />,
          label: i18n.t("AlignToPrevious") || "Önceki elemente hizala",
          disabled: !hasPrevNeighbor,
          onClick: () => onAlignToNeighbor(layer.id, "previous"),
        }),
      onAlignToNeighbor &&
        !layer.locked &&
        menuBtn({
          key: "align-next",
          icon: <LuAlignHorizontalJustifyEnd />,
          label: i18n.t("AlignToNext") || "Sonraki elemente hizala",
          disabled: !hasNextNeighbor,
          onClick: () => onAlignToNeighbor(layer.id, "next"),
        }),
    ]);

    pushMenuGroup((extraItems || []).map(menuBtn));

    pushMenuGroup([
      onDelete &&
        menuBtn({
          key: "delete",
          icon: <LuTrash2 />,
          label: i18n.t("Remove") || "Kaldır",
          tone: "danger",
          onClick: () => onDelete(layer.id),
        }),
    ]);

    pushMenuGroup([
      menuBtn({
        key: "cancel",
        icon: <LuX />,
        label: i18n.t("Cancel") || "İptal",
        tone: "cancel",
        onClick: () => {},
      }),
    ]);
  }

  return (
    <>
      <div
        className={[
          "it-clip",
          `it-clip--${layer.type}`,
          isSelected ? "it-clip--selected" : "",
          isNewFlash ? "it-clip--new" : "",
          dragging ? "it-clip--dragging" : "",
          isDragSource ? "it-clip--drag-source" : "",
          resizing ? `it-clip--resizing-${resizing}` : "",
          layer.visible === false ? "it-clip--hidden" : "",
          layer.locked ? "it-clip--locked" : "",
          isSplitHighlighted ? "it-clip--split-highlight" : "",
        ].join(" ")}
        data-layer-id={layer.id}
        style={{ left: clipLeft, width: clipWidth, "--clip-color": color }}
        onPointerDown={handleClipPointerDown}
        onPointerEnter={(e) => {
          if (layer.splitGroup) onSplitGroupHover?.(layer.splitGroup);
          if (isAudio && layer.name) {
            setAudioNameTip(audioTipPosition(e.clientX, e.clientY));
          }
        }}
        onPointerMove={(e) => {
          if (isAudio && audioNameTip) moveAudioTip(e.clientX, e.clientY);
        }}
        onPointerLeave={() => {
          if (layer.splitGroup) onSplitGroupHover?.(null);
          setAudioNameTip(null);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onDoubleClick?.(layer.id);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (menuSelects) onSelect?.(layer.id);
          // Kept on screen against the menu's MAXIMUM size, so the caption
          // growing on hover never repositions it — see MENU_MAX_H.
          const place = (at, span, viewport) =>
            Math.max(MENU_MARGIN, Math.min(at, viewport - span - MENU_MARGIN));
          setContextMenu({
            x: place(e.clientX, MENU_MAX_W, window.innerWidth),
            y: place(e.clientY, menuLabels ? MENU_LABELED_MAX_H : MENU_MAX_H, window.innerHeight),
          });
        }}
      >
        {layer.splitGroup && (
          <span
            className="it-clip-badge it-clip-badge--split"
            title={i18n.t("SplitClipSourceSame") || "Bölünmüş parça — kaynağı aynı"}
          >
            <LuLink size={9} />
          </span>
        )}

        {layer.videoProjectId && (
          <span
            className={`it-clip-loop-badge it-clip-director-badge${
              layer.audioData?.loopScene ? " it-clip-director-badge--beside-loop" : ""
            }`}
            title={i18n.t("OpenInVideoDirector")}
          >
            <LuClapperboard size={9} />
          </span>
        )}

        {!noResize && (
          <div className="it-clip-resize it-clip-resize--left" onPointerDown={handleResizeLeft}>
            <div className="it-clip-resize__bar" />
          </div>
        )}

        {entryW > 0 && (
          <div
            className="it-clip-anim-bar it-clip-anim-bar--entry"
            style={{ width: entryW }}
            title={i18n.t("EntranceAnimation") || "Giriş animasyonu"}
          />
        )}

        {expandable && clipWidth >= 24 && (
          <button
            type="button"
            className="it-clip-expander"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
            title={expanded ? "Daralt" : "Genişlet"}
          >
            {expanded ? <LuChevronDown size={12} /> : <LuChevronRight size={12} />}
          </button>
        )}

        {showTimeBadges && (
          <span className={`it-clip-badge it-clip-badge--start${badgeFlow}`}>
            {toSec(layer.startFrame)}s
          </span>
        )}

        {layer.type === "scene-audio" && layer.audioData?.loopScene && (
          <span className="it-clip-loop-badge" title={i18n.t("messages.trackClip.repeatForScene")}>
            <LuRepeat size={9} />
          </span>
        )}

        {/* Name tooltip: the label ellipsizes on a short clip, and unlike audio
            (which has its own hover card) there was no way to read the rest. */}
        <div className="it-clip-inner" title={isAudio ? undefined : layer.name}>
          {isAudio ? (
            <Waveform
              clipWidth={clipWidth}
              url={waveformUrl}
              trimStartSec={waveformTrimStartSec}
              trimEndSec={waveformTrimEndSec}
            />
          ) : (
            <span className="it-clip-label">{layer.name}</span>
          )}
        </div>

        {/* After .it-clip-inner on purpose: in flow the DOM order is the layout
            order, so an end badge declared before the label would render to the
            LEFT of it. Absolutely positioned (audio) it lands the same either
            way, via `right`. */}
        {showTimeBadges && !noResize && (
          <span className={`it-clip-badge it-clip-badge--end${badgeFlow}`}>
            {toSec(layer.endFrame)}s
          </span>
        )}

        {showPlayWindow &&
          (isSelected ? (
            <div
              className={`it-play-window${pwIsFull ? " it-play-window--full" : ""}`}
              style={{ left: pwLeft, width: pwWidth }}
              onPointerDown={handlePwPointerDown}
              title={`Oynatma penceresi — kaynak ${toSec(pwTrimStart)}s → ${toSec(
                typeof pwTrimEnd === "number" ? pwTrimEnd : pwTrimStart + pwPlayLen
              )}s, sahnede ${toSec(pwPlayStart)}s'de oynar`}
            >
              {hasKnownDuration && (
                <div
                  className="it-play-window-resize it-play-window-resize--left"
                  onPointerDown={handlePwResizeLeft}
                >
                  <div className="it-play-window-resize__bar" />
                </div>
              )}
              <span className="it-play-window__label">
                <LuPlay size={9} />
                {pwWidth >= 42 && <span>{toSec(pwPlayLen)}s</span>}
              </span>
              <div
                className="it-play-window-resize it-play-window-resize--right"
                onPointerDown={handlePwResizeRight}
              >
                <div className="it-play-window-resize__bar" />
              </div>
            </div>
          ) : (
            !pwIsFull && (
              <div
                className="it-play-window it-play-window--strip"
                style={{ left: pwLeft, width: pwWidth }}
              />
            )
          ))}

        {exitW > 0 && (
          <div
            className="it-clip-anim-bar it-clip-anim-bar--exit"
            style={{ width: exitW }}
            title={i18n.t("ExitAnimation") || "Çıkış animasyonu"}
          />
        )}

        {trimHeadHover && canTrimHead && (
          <div className="it-clip-trimhead" style={{ width: trimHeadWidth }}>
            <span className="it-clip-trimhead__label">
              <LuArrowLeftToLine size={10} />
              {trimHeadWidth >= 54 && <span>-{toSec(trimHeadFrames)}s</span>}
            </span>
          </div>
        )}

        {!noResize && (
          <div className="it-clip-resize it-clip-resize--right" onPointerDown={handleResizeRight}>
            <div className="it-clip-resize__bar" />
          </div>
        )}
      </div>

      {isAudio &&
        audioNameTip &&
        createPortal(
          <div
            ref={audioNameTipRef}
            className={`it-audio-name-tip${audioNameTip.below ? " it-audio-name-tip--below" : ""}`}
            style={{ left: audioNameTip.left, top: audioNameTip.top }}
            role="tooltip"
          >
            <span className="it-audio-name-tip__dot" style={{ background: color }} />
            <span className="it-audio-name-tip__text">{layer.name}</span>
          </div>,
          document.body
        )}

      {/* Sağ tık context menu */}
      {contextMenu &&
        createPortal(
          <>
            <div
              style={{ position: "fixed", inset: 0, zIndex: MENU_Z }}
              onPointerDown={(e) => {
                e.stopPropagation(); // React bubbles portal events: keep the track's seek handler out of it
                closeContextMenu();
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeContextMenu();
              }}
            />
            <div
              role="menu"
              className={`it-clip-context-menu${menuLabels ? " it-clip-context-menu--labeled" : ""}`}
              style={{
                position: "fixed",
                left: contextMenu.x,
                top: contextMenu.y,
                zIndex: MENU_Z + 1,
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseLeave={() => setMenuHint(null)}
            >
              <div className="it-clip-context-menu__actions">
                {menuGroups.map((group, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <span className="it-clip-context-menu__divider" />}
                    {group}
                  </React.Fragment>
                ))}
              </div>
              <div className="it-clip-context-menu__caption">
                <span className="it-clip-context-menu__caption-label">
                  {menuHint ? menuHint.label : layer.name}
                </span>
                {menuHint && menuHint.hint && (
                  <span className="it-clip-context-menu__caption-hint">{menuHint.hint}</span>
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}

export default TrackClip;
